import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Garage.module.css';
import carBikeImage from '../../assets/car-bike.png';
import DocumentUploadModal from '../../components/DocumentUploadModal';

// Document type mapping
const typeMap = {
  'RC': 'Registration Certificate',
  'DL': 'Driver License',
  'PUC': 'PUC Certificate',
  'INSURANCE': 'Insurance',
  'Insurance': 'Insurance',
  'FITNESS': 'Fitness Permit',
  'Fitness': 'Fitness Permit',
  'PERMIT': 'Permit',
  'Permit': 'Permit',
  'OTHER': 'Other',
  'Other': 'Other'
};

function Garage() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(localStorage.getItem('vehicle_id'));
  const [vehicleImages, setVehicleImages] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadVehicleId, setUploadVehicleId] = useState(null);
  const [showDocuments, setShowDocuments] = useState({});
  const [deletingDocId, setDeletingDocId] = useState(null);

  // Fetch vehicle reference images
  const fetchVehicleImages = useCallback(async (vehiclesList) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const imagePromises = vehiclesList.map(async (vehicle) => {
      try {
        // If vehicle already has an uploaded image, use it
        if (vehicle.vehicle_image) {
          return { vehicleId: vehicle.id, imageUrl: vehicle.vehicle_image };
        }

        // Fetch reference image from API
        const response = await fetch(
          `https://api.rtoxpert.com/api/vehicles/reference-image?make=${encodeURIComponent(vehicle.brand)}&model=${encodeURIComponent(vehicle.model)}&color=${encodeURIComponent(vehicle.color || '')}&type=${encodeURIComponent(vehicle.type || 'car')}`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.image_url) {
            return { vehicleId: vehicle.id, imageUrl: data.image_url };
          }
        }
        return { vehicleId: vehicle.id, imageUrl: null };
      } catch (err) {
        console.error('Error fetching image for vehicle:', vehicle.id, err);
        return { vehicleId: vehicle.id, imageUrl: null };
      }
    });

    const results = await Promise.all(imagePromises);
    const imagesMap = {};
    results.forEach(({ vehicleId, imageUrl }) => {
      imagesMap[vehicleId] = imageUrl;
    });
    setVehicleImages(imagesMap);
  }, []);

  // Fetch vehicles function
  const fetchVehicles = useCallback(async () => {
    const token = localStorage.getItem('token');
    const user_id = localStorage.getItem('user_id');
    if (!token || !user_id) {
      setError('Authentication information missing. Please log in again.');
      setIsLoading(false);
      return;
    }

    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'GET',
      headers: myHeaders,
      redirect: 'follow',
    };

    try {
      const response = await fetch(`https://api.rtoxpert.com/api/vehicles/user/${user_id}`, requestOptions);

      if (response.status === 401) {
        setError('Session expired. Please log in again.');
        localStorage.removeItem('token');
        localStorage.removeItem('user_id');
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch vehicles');
      }
      const result = await response.json();

      const mappedVehicles = result.vehicles.map((vehicle) => ({
        id: vehicle.vehicle_id,
        brand: vehicle.brand,
        model: vehicle.model,
        vehicleNumber: vehicle.vehicle_number,
        type: vehicle.vehicle_type,
        yearOfRegistration: vehicle.year_of_registration || null,
        fuelType: vehicle.fuel_type || 'unknown',
        insuranceStatus: vehicle.insurance_status || 'false',
        insuranceExpiry: vehicle.insurance_expiry ? new Date(vehicle.insurance_expiry).toISOString().split('T')[0] : null,
        pucExpiry: vehicle.puc_expiry ? new Date(vehicle.puc_expiry).toISOString().split('T')[0] : null,
        fitnessExpiry: vehicle.fitness_expiry ? new Date(vehicle.fitness_expiry).toISOString().split('T')[0] : null,
        registrationDate: vehicle.registration_date ? new Date(vehicle.registration_date).toISOString().split('T')[0] : null,
        taxExpiry: vehicle.tax_expiry ? new Date(vehicle.tax_expiry).toISOString().split('T')[0] : null,
        isCommercial: vehicle.is_commercial || false,
        permitExpiry: vehicle.permit_expiry ? new Date(vehicle.permit_expiry).toISOString().split('T')[0] : null,
        color: vehicle.color || null,
        bodyType: vehicle.body_type || null,
        cubicCapacity: vehicle.cubic_capacity ? `${vehicle.cubic_capacity} CC` : null,
        registeredAt: vehicle.registered_at || null,
        normsType: vehicle.norms_type || null,
        blacklistStatus: vehicle.blacklist_status === 'false' ? 'Not Blacklisted' : 'Blacklisted',
        vehicle_image: vehicle.vehicle_image || null,
        documents: Array.isArray(vehicle.documents)
          ? vehicle.documents.map((doc) => ({
            ...doc,
            _id: doc._id,
            document_id: doc.document_id,
            document_type: doc.document_type,
            file_path: doc.file_path,
            file_name: doc.file_name,
            expiry_date: doc.expiry_date ? new Date(doc.expiry_date).toISOString().split('T')[0] : null,
          }))
          : [],
      }));
      setVehicles(mappedVehicles);

      if (!localStorage.getItem('vehicle_id') && mappedVehicles.length > 0) {
        const firstVehicleId = mappedVehicles[0].id;
        localStorage.setItem('vehicle_id', firstVehicleId);
        setSelectedVehicleId(firstVehicleId);
      }

      // Fetch reference images for all vehicles
      fetchVehicleImages(mappedVehicles);
    } catch (error) {
      setError(error.message);
      console.error('Fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchVehicleImages]);

  // Initial fetch and visibility change listener for real-time refresh
  useEffect(() => {
    fetchVehicles();

    // Refresh data when page becomes visible (tab focus)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchVehicles();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchVehicles]);

  // Handle document upload
  const handleUploadDocument = (vehicleId) => {
    setUploadVehicleId(vehicleId);
    setShowUploadModal(true);
  };

  const handleUploadSuccess = () => {
    fetchVehicles(); // Refresh vehicles list after upload
  };

  // Handle document delete
  const handleDeleteDocument = async (vehicleId, docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    setDeletingDocId(docId);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(
        `https://api.rtoxpert.com/api/vehicles/${vehicleId}/documents/${docId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || 'Failed to delete document');
      }

      fetchVehicles(); // Refresh list
    } catch (err) {
      alert(err.message || 'Failed to delete document');
    } finally {
      setDeletingDocId(null);
    }
  };

  // Handle document view
  const handleViewDocument = (filePath) => {
    if (filePath && filePath.startsWith('http')) {
      window.open(filePath, '_blank');
    } else {
      alert('Document not available');
    }
  };

  // Toggle documents visibility for a vehicle
  const toggleDocuments = (vehicleId) => {
    setShowDocuments(prev => ({
      ...prev,
      [vehicleId]: !prev[vehicleId]
    }));
  };

  const handleSelectVehicle = (id) => {
    localStorage.setItem('vehicle_id', id);
    setSelectedVehicleId(id);
  };

  const handleDeleteVehicle = (id) => {
    setVehicleToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteVehicle = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token missing. Please log in again.');
      setShowDeleteConfirm(false);
      return;
    }

    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'DELETE',
      headers: myHeaders,
      redirect: 'follow',
    };

    try {
      const response = await fetch(`https://api.rtoxpert.com/api/vehicles/${vehicleToDelete}`, requestOptions);
      if (!response.ok) {
        throw new Error('Failed to delete vehicle');
      }
      const result = await response.text();
      console.log(result);

      const newVehicles = vehicles.filter((vehicle) => vehicle.id !== vehicleToDelete);
      setVehicles(newVehicles);

      if (vehicleToDelete === selectedVehicleId) {
        if (newVehicles.length > 0) {
          const newSelectedId = newVehicles[0].id;
          localStorage.setItem('vehicle_id', newSelectedId);
          setSelectedVehicleId(newSelectedId);
        } else {
          localStorage.removeItem('vehicle_id');
          setSelectedVehicleId(null);
        }
      }
    } catch (error) {
      setError(error.message);
      console.error(error);
    } finally {
      setShowDeleteConfirm(false);
      setVehicleToDelete(null);
    }
  };

  const cancelDeleteVehicle = () => {
    setShowDeleteConfirm(false);
    setVehicleToDelete(null);
  };

  const handleAddVehicle = () => {
    navigate('/addYourVehicle');
  };

  const handleEditVehicle = (vehicle) => {
    setEditingVehicle({ ...vehicle, documents: Array.isArray(vehicle.documents) ? vehicle.documents : [] });
    setIsEditing(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditingVehicle({
      ...editingVehicle,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token missing. Please log in again.');
      return;
    }

    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);
    myHeaders.append('Content-Type', 'application/json');

    const updatedFields = {
      brand: editingVehicle.brand,
      model: editingVehicle.model,
      vehicle_number: editingVehicle.vehicleNumber,
      vehicle_type: editingVehicle.type,
      year_of_registration: editingVehicle.yearOfRegistration,
      fuel_type: editingVehicle.fuelType,
      insurance_status: editingVehicle.insuranceStatus,
      insurance_expiry: editingVehicle.insuranceExpiry,
      puc_expiry: editingVehicle.pucExpiry,
      fitness_expiry: editingVehicle.fitnessExpiry,
      registration_date: editingVehicle.registrationDate,
      tax_expiry: editingVehicle.taxExpiry,
      is_commercial: editingVehicle.isCommercial,
      permit_expiry: editingVehicle.permitExpiry,
      color: editingVehicle.color,
      body_type: editingVehicle.bodyType,
      cubic_capacity: editingVehicle.cubicCapacity?.replace(' CC', '') || '',
      registered_at: editingVehicle.registeredAt,
      norms_type: editingVehicle.normsType,
      blacklist_status: editingVehicle.blacklistStatus === 'Not Blacklisted' ? 'false' : 'true',
    };

    const requestOptions = {
      method: 'PUT',
      headers: myHeaders,
      body: JSON.stringify(updatedFields),
      redirect: 'follow',
    };

    try {
      const response = await fetch(`https://api.rtoxpert.com/api/vehicles/${editingVehicle.id}`, requestOptions);
      if (!response.ok) {
        throw new Error('Failed to update vehicle');
      }
      const result = await response.text();
      console.log(result);

      setVehicles(vehicles.map((v) => (v.id === editingVehicle.id ? editingVehicle : v)));
      setIsEditing(false);
      setEditingVehicle(null);
    } catch (error) {
      setError(error.message);
      console.error(error);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingVehicle(null);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (isEditing) {
    return (
      <div className={`${styles.container} ${styles.editMode}`}>
        <div className={styles.formSection}>
          <h1 className={styles.title}>Edit Vaahan</h1>

          <form onSubmit={handleSubmit}>
            <div className={styles.inputGroup}>
              <label>Brand</label>
              <input
                type="text"
                name="brand"
                placeholder="Brand"
                className={styles.inputField}
                value={editingVehicle.brand}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Model</label>
              <input
                type="text"
                name="model"
                placeholder="Model"
                className={styles.inputField}
                value={editingVehicle.model}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Vaahan Number</label>
              <input
                type="text"
                name="vehicleNumber"
                placeholder="Vaahan Number"
                className={styles.inputField}
                value={editingVehicle.vehicleNumber}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Vaahan Type</label>
              <select
                name="type"
                className={styles.inputField}
                value={editingVehicle.type}
                onChange={handleInputChange}
              >
                <option value="car">Car</option>
                <option value="bike">Bike</option>
                <option value="scooter">Scooter</option>
              </select>
            </div>

            <div className={styles.inputGroup}>
              <label>Year of Registration</label>
              <input
                type="number"
                name="yearOfRegistration"
                placeholder="Year of Registration"
                className={styles.inputField}
                value={editingVehicle.yearOfRegistration}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Fuel Type</label>
              <select
                name="fuelType"
                className={styles.inputField}
                value={editingVehicle.fuelType}
                onChange={handleInputChange}
              >
                <option value="petrol">Petrol</option>
                <option value="diesel">Diesel</option>
                <option value="electric">Electric</option>
                <option value="cng">CNG</option>
                <option value="petrol/cng">Petrol/CNG</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>

            <div className={styles.inputGroup}>
              <label>Insurance Expiry</label>
              <input
                type="date"
                name="insuranceExpiry"
                className={styles.inputField}
                value={editingVehicle.insuranceExpiry || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>PUC Expiry</label>
              <input
                type="date"
                name="pucExpiry"
                className={styles.inputField}
                value={editingVehicle.pucExpiry || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Fitness Expiry</label>
              <input
                type="date"
                name="fitnessExpiry"
                className={styles.inputField}
                value={editingVehicle.fitnessExpiry || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Registration Date</label>
              <input
                type="date"
                name="registrationDate"
                className={styles.inputField}
                value={editingVehicle.registrationDate || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Color</label>
              <input
                type="text"
                name="color"
                placeholder="Color"
                className={styles.inputField}
                value={editingVehicle.color || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Body Type</label>
              <input
                type="text"
                name="bodyType"
                placeholder="Body Type"
                className={styles.inputField}
                value={editingVehicle.bodyType || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Cubic Capacity</label>
              <input
                type="text"
                name="cubicCapacity"
                placeholder="Cubic Capacity"
                className={styles.inputField}
                value={editingVehicle.cubicCapacity || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Registered At</label>
              <input
                type="text"
                name="registeredAt"
                placeholder="Registered At"
                className={styles.inputField}
                value={editingVehicle.registeredAt || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Norms Type</label>
              <input
                type="text"
                name="normsType"
                placeholder="Norms Type"
                className={styles.inputField}
                value={editingVehicle.normsType || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className={styles.inputGroup}>
              <label>Blacklist Status</label>
              <select
                name="blacklistStatus"
                className={styles.inputField}
                value={editingVehicle.blacklistStatus}
                onChange={handleInputChange}
              >
                <option value="Not Blacklisted">Not Blacklisted</option>
                <option value="Blacklisted">Blacklisted</option>
              </select>
            </div>

            {editingVehicle.isCommercial && (
              <>
                <div className={styles.inputGroup}>
                  <label>Tax Expiry</label>
                  <input
                    type="date"
                    name="taxExpiry"
                    className={styles.inputField}
                    value={editingVehicle.taxExpiry || ''}
                    onChange={handleInputChange}
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label>Permit Expiry</label>
                  <input
                    type="date"
                    name="permitExpiry"
                    className={styles.inputField}
                    value={editingVehicle.permitExpiry || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </>
            )}

            <div className={styles.inputGroup}>
              <label>Commercial Vaahan</label>
              <select
                name="isCommercial"
                className={styles.inputField}
                value={editingVehicle.isCommercial}
                onChange={(e) =>
                  setEditingVehicle({
                    ...editingVehicle,
                    isCommercial: e.target.value === 'true',
                  })
                }
              >
                <option value={true}>Yes</option>
                <option value={false}>No</option>
              </select>
            </div>

            <div className={styles.buttonGroup}>
              <button type="submit" className={styles.submitButton}>
                Save Changes
              </button>
              <button type="button" className={styles.cancelButton} onClick={handleCancelEdit}>
                Cancel
              </button>
            </div>
          </form>
        </div>

        <div className={styles.imageSection}>
          <img
            src={carBikeImage}
            alt="Vaahan images showing SUV and motorcycle"
            className={styles.vehicleImage}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>MyExpeditions</h1>
        <button className={styles.addButton} onClick={handleAddVehicle}>
          <svg className={styles.plusIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          Add Vaahan
        </button>
      </div>

      {vehicles.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIconContainer}>
            <svg className={styles.emptyIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 9h-6V3H9v6H3v2h6v6h4v-6h6z"></path>
            </svg>
          </div>
          <h2 className={styles.emptyTitle}>No vaahans added yet</h2>
          <p className={styles.emptyMessage}>Click the 'Add Vaahan' button to get started</p>
        </div>
      ) : (
        <div className={styles.vehicleGrid}>
          {vehicles.map((vehicle) => (
            <div
              key={vehicle.id}
              className={`${styles.vehicleCard} ${vehicle.id === selectedVehicleId ? styles.selected : ''}`}
              onClick={() => handleSelectVehicle(vehicle.id)}
            >
              {/* Vehicle Reference Image */}
              <div className={styles.vehicleImageContainer}>
                <img
                  src={vehicleImages[vehicle.id] || vehicle.vehicle_image || carBikeImage}
                  alt={`${vehicle.brand} ${vehicle.model}`}
                  className={styles.vehicleRefImage}
                  onError={(e) => { e.target.src = carBikeImage; }}
                />
                {vehicleImages[vehicle.id] && (
                  <span className={styles.referenceLabel}>Reference</span>
                )}
                {vehicle.id === selectedVehicleId && (
                  <span className={styles.selectedBadge}>✓ Selected</span>
                )}
              </div>

              <div className={styles.vehicleHeader}>
                <div className={styles.vehicleHeaderContent}>
                  <h3 className={styles.vehicleName}>{`${vehicle.brand} ${vehicle.model}`}</h3>
                  <span className={styles.vehicleType}>{vehicle.type}</span>
                </div>
                <p className={styles.vehicleNumber}>{vehicle.vehicleNumber}</p>
              </div>

              <div className={styles.vehicleBody}>
                <div className={styles.infoRow}>
                  <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <span className={styles.infoText}>
                    Registration:{' '}
                    {vehicle.registrationDate
                      ? new Date(vehicle.registrationDate).toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric',
                      })
                      : 'N/A'}
                  </span>
                </div>

                <div className={styles.infoRow}>
                  <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19V5m0 14h-2a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2v10a2 2 0 01-2 2h-2z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5h4a2 2 0 012 2v3a2 2 0 01-2 2h-4"></path>
                  </svg>
                  <span className={styles.infoText}>Fuel: {vehicle.fuelType || 'N/A'}</span>
                </div>

                <div className={styles.infoRow}>
                  <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2c-4.97 0-9 4.03-9 9v6l9 5 9-5v-6c0-4.97-4.03-9-9-9z"></path>
                  </svg>
                  <span className={styles.infoText}>
                    Insurance:{' '}
                    {vehicle.insuranceExpiry
                      ? new Date(vehicle.insuranceExpiry).toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric',
                      })
                      : 'N/A'}
                  </span>
                </div>

                <div className={styles.infoRow}>
                  <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 3.784c-2.24 0-4.32.608-6.043 1.638"></path>
                  </svg>
                  <span className={styles.infoText}>
                    PUC:{' '}
                    {vehicle.pucExpiry
                      ? new Date(vehicle.pucExpiry).toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric',
                      })
                      : 'N/A'}
                  </span>
                </div>

                <div className={styles.infoRow}>
                  <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path>
                  </svg>
                  <span className={styles.infoText}>
                    Fitness:{' '}
                    {vehicle.fitnessExpiry
                      ? new Date(vehicle.fitnessExpiry).toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric',
                      })
                      : 'N/A'}
                  </span>
                </div>

                <div className={styles.infoRow}>
                  <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h10a2 2 0 012 2v14a2 2 0 01-2 2z"></path>
                  </svg>
                  <span className={styles.infoText}>
                    Document Expiry:{' '}
                    {vehicle.documents && vehicle.documents.length > 0 && vehicle.documents[0].expiry_date
                      ? new Date(vehicle.documents[0].expiry_date).toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric',
                      })
                      : 'N/A'}
                  </span>
                </div>

                {/* <div className={styles.infoRow}>
                  <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h10M7 11h10M7 15h10"></path>
                  </svg>
                  <span className={styles.infoText}>Color: {vehicle.color || 'N/A'}</span>
                </div> */}

                {/* <div className={styles.infoRow}>
                  <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                  </svg>
                  <span className={styles.infoText}>Body Type: {vehicle.bodyType || 'N/A'}</span>
                </div> */}

                {/* <div className={styles.infoRow}>
                  <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20h4a2 2 0 002-2V6a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <span className={styles.infoText}>Cubic Capacity: {vehicle.cubicCapacity || 'N/A'}</span>
                </div> */}

                <div className={styles.infoRow}>
                  <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                  </svg>
                  <span className={styles.infoText}>Registered At: {vehicle.registeredAt || 'N/A'}</span>
                </div>

                {/* <div className={styles.infoRow}>
                  <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 3.784c-2.24 0-4.32.608-6.043 1.638"></path>
                  </svg>
                  <span className={styles.infoText}>Norms Type: {vehicle.normsType || 'N/A'}</span>
                </div> */}

                {/* <div className={styles.infoRow}>
                  <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                  </svg>
                  <span className={styles.infoText}>Blacklist Status: {vehicle.blacklistStatus || 'N/A'}</span>
                </div> */}

                {vehicle.isCommercial && (
                  <>
                    <div className={styles.infoRow}>
                      <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a5 5 0 00-5-5H7a5 5 0 00-5 5v6a5 5 0 005 5h2"></path>
                      </svg>
                      <span className={styles.infoText}>
                        Tax:{' '}
                        {vehicle.taxExpiry
                          ? new Date(vehicle.taxExpiry).toLocaleDateString('en-US', {
                            month: 'numeric',
                            day: 'numeric',
                            year: 'numeric',
                          })
                          : 'N/A'}
                      </span>
                    </div>
                    <div className={styles.infoRow}>
                      <svg className={styles.infoIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6a2 2 0 002-2V7"></path>
                      </svg>
                      <span className={styles.infoText}>
                        Permit:{' '}
                        {vehicle.permitExpiry
                          ? new Date(vehicle.permitExpiry).toLocaleDateString('en-US', {
                            month: 'numeric',
                            day: 'numeric',
                            year: 'numeric',
                          })
                          : 'N/A'}
                      </span>
                    </div>
                  </>
                )}

                <div className={styles.actionButtons}>
                  <button
                    className={styles.editButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditVehicle(vehicle);
                    }}
                  >
                    <svg className={styles.actionIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
                    </svg>
                    Edit
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteVehicle(vehicle.id);
                    }}
                    className={styles.deleteButton}
                  >
                    <svg className={styles.actionIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                    Delete
                  </button>
                </div>

                {/* Document Section */}
                <div className={styles.documentSection}>
                  <div className={styles.documentHeader}>
                    <h4 className={styles.documentTitle}>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={styles.docIcon}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Documents ({vehicle.documents?.length || 0})
                    </h4>
                    <div className={styles.documentActions}>
                      <button
                        className={styles.uploadButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUploadDocument(vehicle.id);
                        }}
                      >
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Upload
                      </button>
                      {vehicle.documents?.length > 0 && (
                        <button
                          className={styles.toggleDocsBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDocuments(vehicle.id);
                          }}
                        >
                          {showDocuments[vehicle.id] ? 'Hide' : 'Show'}
                        </button>
                      )}
                    </div>
                  </div>

                  {showDocuments[vehicle.id] && vehicle.documents?.length > 0 && (
                    <div className={styles.documentList}>
                      {vehicle.documents.map((doc) => (
                        <div key={doc._id || doc.document_id} className={styles.documentItem}>
                          <div className={styles.documentInfo}>
                            <span className={styles.documentTypeBadge}>
                              {typeMap[doc.document_type] || doc.document_type || 'Document'}
                            </span>
                            <span className={styles.documentName}>
                              {doc.file_name || 'Document'}
                            </span>
                            {doc.expiry_date && (
                              <span className={styles.documentExpiry}>
                                Exp: {new Date(doc.expiry_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <div className={styles.documentItemActions}>
                            <button
                              className={styles.viewDocBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDocument(doc.file_path);
                              }}
                            >
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <button
                              className={styles.deleteDocBtn}
                              disabled={deletingDocId === (doc._id || doc.document_id)}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDocument(vehicle.id, doc._id || doc.document_id);
                              }}
                            >
                              {deletingDocId === (doc._id || doc.document_id) ? (
                                <span className={styles.spinner}></span>
                              ) : (
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDeleteConfirm && (
        <div className={styles.deleteConfirmPopup}>
          <div className={styles.popupContent}>
            <h3>Are you sure you want to delete this vaahan?</h3>
            <div className={styles.popupButtons}>
              <button onClick={confirmDeleteVehicle} className={styles.confirmButton}>
                Yes
              </button>
              <button onClick={cancelDeleteVehicle} className={styles.cancelButton}>
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      <DocumentUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        vehicleId={uploadVehicleId}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
}

export default Garage;